/**
 * PlaceCoreHero — Full-width hero with title + subtitle only.
 * Supports swipeable multi-image hero via HeroCarousel when multiple images exist.
 * Badges and verified status moved to content section for cleaner editorial feel.
 */
import { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Share2, MapPin } from "lucide-react";
import { FavoriteButton } from "@/components/favorites/FavoriteButton";
import { HeroCarousel, type HeroImage } from "@/components/place/HeroCarousel";
import { track } from "@/lib/analytics";
import { useTranslate } from "@/i18n";

interface Props {
  title: string;
  subtitle: string;
  /** Short ingress shown under title (description_short). */
  descriptionShort?: string | null;
  /** Single hero image URL (fallback, used when heroImages is empty). */
  heroImageUrl: string | null;
  /** All hero images for carousel. Takes precedence over heroImageUrl. */
  heroImages?: HeroImage[];
  /** Place metadata for analytics + favorites */
  placeId?: string;
  placeSlug?: string | null;
  placeName?: string;
  onShare: () => void;
  /** Optional: render share action in hero content on mobile (used by Area page). */
  mobileShareInHero?: boolean;
  /** Controlled active image index for hero carousel. */
  activeIndex?: number;
  /** Called when user swipes hero to a new index. */
  onHeroIndexChange?: (index: number) => void;
}

export function PlaceCoreHero({
  title,
  subtitle,
  descriptionShort,
  heroImageUrl,
  heroImages,
  placeId,
  placeSlug,
  placeName,
  onShare,
  mobileShareInHero = false,
  activeIndex,
  onHeroIndexChange,
}: Props) {
  const t = useTranslate();
  // Build final image list: prefer heroImages, fallback to single heroImageUrl
  const images: HeroImage[] =
    heroImages && heroImages.length > 0
      ? heroImages
      : heroImageUrl
        ? [{ id: "cover", src: heroImageUrl, alt: title }]
        : [];

  const hasCover = images.length > 0;

  const handleIndexChange = useCallback(
    (index: number) => {
      onHeroIndexChange?.(index);
      if (!placeId) return;
      track("place_hero_image_changed", {
        place_id: placeId,
        place_slug: placeSlug ?? null,
        index,
        images_count: images.length,
      });
    },
    [placeId, placeSlug, images.length, onHeroIndexChange],
  );

  return (
    <header className="relative">
      {hasCover ? (
        <div className="relative aspect-[4/3] sm:aspect-[16/9] md:aspect-[2.4/1] w-full overflow-hidden">
          <HeroCarousel
            images={images}
            className="absolute inset-0 w-full h-full"
            onIndexChange={handleIndexChange}
            activeIndex={activeIndex}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/10 pointer-events-none" />
        </div>
      ) : (
        <div
          className="relative h-48 sm:h-56 md:h-72 overflow-hidden"
          style={{
            background:
              "linear-gradient(160deg, hsl(var(--secondary)) 0%, hsl(var(--muted)) 60%, hsl(var(--accent)) 100%)",
          }}
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-primary/15 rounded-full blur-2xl scale-[2]" />
              <div className="relative bg-primary text-primary-foreground p-4 rounded-full shadow-lg">
                <MapPin className="h-6 w-6" />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={hasCover ? "absolute bottom-0 left-0 right-0 pb-6 md:pb-10 pt-20 pointer-events-none" : "pb-0"}>
        <div className="container">
          <div className="max-w-2xl">
            <h1
              className={`font-display text-2xl sm:text-3xl lg:text-4xl font-medium leading-tight mb-1 ${
                hasCover ? "text-white" : "text-foreground pt-6 md:pt-8"
              }`}
            >
              {title}
            </h1>

            <p
              className={`text-sm sm:text-base mb-4 flex items-center gap-1.5 ${
                hasCover ? "text-white/75" : "text-muted-foreground"
              }`}
            >
              <MapPin className="h-3.5 w-3.5 shrink-0" />
              {subtitle}
            </p>

            {/* Short ingress — description_short */}
            {descriptionShort && (
              <p
                className={`text-sm sm:text-base leading-relaxed line-clamp-2 mb-4 max-w-xl ${
                  hasCover ? "text-white/70" : "text-muted-foreground"
                }`}
              >
                {descriptionShort}
              </p>
            )}

            {mobileShareInHero && (
              <div className="md:hidden mb-4 pointer-events-auto">
                <Button
                  variant="outline"
                  size="sm"
                  className={`rounded-full ${
                    hasCover
                      ? "bg-white/15 text-white border-white/30 hover:bg-white/25 backdrop-blur-sm"
                      : ""
                  }`}
                  onClick={onShare}
                >
                  <Share2 className="h-3.5 w-3.5 mr-1.5" />
                  {t("cta.share")}
                </Button>
              </div>
            )}

            <div className="hidden md:flex gap-2 pointer-events-auto">
              <Button
                variant="outline"
                size="sm"
                className={`rounded-full ${
                  hasCover
                    ? "bg-white/15 text-white border-white/30 hover:bg-white/25 backdrop-blur-sm"
                    : ""
                }`}
                onClick={onShare}
              >
                <Share2 className="h-3.5 w-3.5 mr-1.5" />
                {t("cta.share")}
              </Button>
              {placeId && (
                <FavoriteButton
                  kind="place"
                  entityId={placeId}
                  slug={placeSlug ?? undefined}
                  title={placeName ?? title}
                  imagePath={heroImageUrl ?? undefined}
                  size="sm"
                  location="detail"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
